import { type AxiosPromise } from "axios";
import React from "react";
import { Button, Message } from "semantic-ui-react";
import { uploadFileToCoreApi } from "utilities/fileUploadUtils";
import FakeProgress from "../components/FakeProgress";
import { type SignType } from "./drawing/models";
import SigningCanvasWithSignButton from "./drawing/SigningCanvasWithSignButton";
import UploadSignatureFile, { type UploadedFileData } from "./UploadSignatureFile";

interface Props {
  secret: string
  signeeName: string
  company?: server.dto.CompanySmall
  onSignClicked: (signValue: SignValue) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.PlaceSignatureOutcome>>
  hasFinishedSigning: () => void
}

export interface SignValue {
  svg?: string
  fileGuid?: string
};

const SignModal = (props: Props) => {
  const [hasFinishedSigning, setHasFinishedSigning] = React.useState<boolean>(undefined);
  const [signingError, setSigningError] = React.useState(false);
  const [signType, setSignType] = React.useState<SignType>("draw");
  const [initialSvgSignature, setInitialSignatureSvg] = React.useState<string>(undefined);
  const [uploadedFile, setUploadedFile] = React.useState<UploadedFileData>(undefined);
  const [isLoadingInitialSignatures, setIsLoadingInitialSignatures] = React.useState<boolean>(true);

  const [uploadError, setUploadError] = React.useState<string>(undefined);

  const fileRef = React.useRef<HTMLInputElement>(undefined);

  React.useEffect(() => {
    if (site.currentUser == null) {
      setIsLoadingInitialSignatures(false);
      return;
    }

    window.CoreApi.ESign.GetLastSignature()
      .then(lastSigRes => {
        if (lastSigRes.data.data == null) {
          setSignType("draw");
          setInitialSignatureSvg("");
        } else if (lastSigRes.data.data.signatureSvg) {
          setSignType("draw");
          setInitialSignatureSvg(lastSigRes.data.data.signatureSvg);
        } else if (lastSigRes.data.data.signatureFileId) {
          setSignType("imageFile");
          setUploadedFile({
            guid: lastSigRes.data.data.signatureFileId,
            url: lastSigRes.data.data.signatureUrl
          });
        }
      })
      .finally(() => { setIsLoadingInitialSignatures(false); });
  }, []);

  const startSigning = async (signValue: SignValue) => {
    setHasFinishedSigning(false);
    try {
      await props.onSignClicked(signValue);
      // await fakeSign();
      setHasFinishedSigning(true);
    } catch (e) {
      setSigningError(true);
      setHasFinishedSigning(undefined);
      throw e;
    }
  };

  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    setUploadError(undefined);
    const file = e.target.files[0];
    if (!file) return;

    const resp = await uploadFileToCoreApi(
      window.CoreApi.ESignOpen.UploadSignatureFile,
      window.CoreApi.ESignOpen.UploadSignatureFile.absoluteUrl(props.secret),
      { file },
      { fileType: "image" }
    );
    if (resp.data.hasErrors) {
      setUploadError(resp.data.errors[0].message);
      return;
    }
    setUploadedFile({
      url: resp.data.data.downloadUrl,
      guid: resp.data.data.fileId
    });
    setSignType("imageFile");
  };

  const selectDrawSignature = () => {
    setSignType("draw");
    setUploadError(undefined);
  };

  const fileRemoved = () => {
    setUploadedFile(undefined);
    setUploadError(undefined);
    setSignType("draw");
  };

  const displayDrawingCanvas = signType === "draw" || (signType === "imageFile" && uploadedFile == null);

  return <>
    <div className="margin-bottom-large">
      <table className="s1_table bordergrid gray">
        <tbody>
          <tr>
            <td>{RESX.GeneralLabels.Name}</td>
            <td className="s1_cell_bigpadding">
              {props.signeeName}
            </td>
          </tr>
          {!!props.company?.companyId && <tr>
            <td>
              {RESX.ESign.Representing}
            </td>
            <td className="s1_cell_bigpadding">
              {props.company.name}
            </td>
          </tr>}
        </tbody>
      </table>
    </div>

    <div className="margin-bottom">
      <Button.Group fluid>
        <Button
          primary
          onClick={selectDrawSignature}
          active={displayDrawingCanvas}>
          {RESX.ESignLanding.btnDraw}
        </Button>
        <Button
          className="blue"
          onClick={() => { fileRef.current.click(); }}
          active={!displayDrawingCanvas}>
          {RESX.ESignLanding.btnImage}
        </Button>
      </Button.Group>
    </div>
    {!isLoadingInitialSignatures && <>
      {signingError && <Message className="margin-bottom" color="red">
        <div dangerouslySetInnerHTML={{ __html: RESX.ExceptionMessage.ThereWasAnError }}></div>
      </Message>}

      {uploadError && <Message className="margin-bottom" color="red">
        {uploadError}
      </Message>}

      {displayDrawingCanvas && <div className="margin-bottom-small">
        <SigningCanvasWithSignButton
          initialSvg={initialSvgSignature}
          onSignClicked={async svg => startSigning({ svg })}
        />
      </div>}
      {!displayDrawingCanvas && <div className="margin-bottom-small">
        <UploadSignatureFile
          uploadedFile={uploadedFile}
          onFileRemoved={fileRemoved}
          onSign={async guid => startSigning({ fileGuid: guid })}
        />
      </div>}

      {hasFinishedSigning != null && <div>
        <FakeProgress speed="slowest" isDone={hasFinishedSigning} onFinish={props.hasFinishedSigning} />
      </div>}
    </>}

    <input ref={fileRef} type="file" accept="image/*" onChange={onFileSelected} style={{ display: "none" }} />
  </>;
};

export default SignModal;